import { useCurrentPlan } from './useCurrentPlan'
import { useState } from 'react'
import {
  type PlanEntitlements,
  getPlanEntitlements,
  isUnlimitedLimit,
} from '@/lib/plan-entitlements'

export interface FeatureLimit extends PlanEntitlements {
  maxEvents: number
  maxProposals: number
  maxPackages: number
  canAccessAdvancedFeatures: boolean
  canUseWaitlist: boolean
  canRemoveBranding: boolean
  canUseWhatsAppTemplates: boolean
  canUseAdvancedRefund: boolean
  canUseAIGeneration: boolean
}

export interface FeatureGateResult {
  isAllowed: boolean
  limit: number
  current: number
  remaining: number
  showUpgradeModal: () => void
  closeUpgradeModal: () => void
  upgradeModalOpen: boolean
}

export interface Usage {
  events: number
  proposals: number
  packages: number
}

/**
 * Hook to check if a feature is available for the current plan
 * and enforce usage limits
 * 
 * @param feature - The feature to check (e.g., 'maxEvents', 'maxProposals', 'canRemoveBranding')
 * @param currentUsage - Current usage count (for countable features)
 * @returns Object with isAllowed flag and upgrade modal controls
 */
export function useFeatureGate(
  feature: keyof FeatureLimit,
  currentUsage?: number
): FeatureGateResult {
  const { plan: currentPlan, isLoading } = useCurrentPlan()
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)

  const baseEntitlements = getPlanEntitlements(currentPlan)
  const planLimit: FeatureLimit = {
    ...baseEntitlements,
    canAccessAdvancedFeatures: currentPlan === 'pro' || currentPlan === 'studio',
    canUseWaitlist: currentPlan === 'pro' || currentPlan === 'studio',
    canRemoveBranding: currentPlan === 'studio',
    canUseWhatsAppTemplates: currentPlan !== 'free',
    canUseAdvancedRefund: currentPlan === 'pro' || currentPlan === 'studio',
    canUseAIGeneration: currentPlan !== 'free',
  }
  const featureValue = planLimit[feature]

  // For boolean features
  if (typeof featureValue === 'boolean') {
    return {
      // During loading, allow access to avoid blocking authenticated users
      isAllowed: isLoading ? true : featureValue,
      limit: 0,
      current: 0,
      remaining: 0,
      showUpgradeModal: () => setUpgradeModalOpen(true),
      closeUpgradeModal: () => setUpgradeModalOpen(false),
      upgradeModalOpen,
    }
  }

  // For numeric limits
  const limit = featureValue as number
  const current = currentUsage || 0
  const remaining = Math.max(0, limit - current)
  // During loading, allow access to avoid blocking authenticated users prematurely
  const isAllowed = isLoading ? true : (isUnlimitedLimit(limit) || current < limit)

  return {
    isAllowed,
    limit,
    current,
    remaining,
    showUpgradeModal: () => setUpgradeModalOpen(true),
    closeUpgradeModal: () => setUpgradeModalOpen(false),
    upgradeModalOpen,
  }
}

/**
 * Hook to get all limits for the current plan
 */
export function usePlanLimits(): FeatureLimit {
  const { plan: currentPlan } = useCurrentPlan()
  const baseEntitlements = getPlanEntitlements(currentPlan)
  return {
    ...baseEntitlements,
    canAccessAdvancedFeatures: currentPlan === 'pro' || currentPlan === 'studio',
    canUseWaitlist: currentPlan === 'pro' || currentPlan === 'studio',
    canRemoveBranding: currentPlan === 'studio',
    canUseWhatsAppTemplates: currentPlan !== 'free',
    canUseAdvancedRefund: currentPlan === 'pro' || currentPlan === 'studio',
    canUseAIGeneration: currentPlan !== 'free',
  }
}

/**
 * Hook to check if user is approaching any limit (>= 80%)
 */
export function useApproachingLimits(usage: Usage): {
  isApproachingAnyLimit: boolean
  approachingLimits: Array<{ feature: string; percentage: number }>
} {
  const limits = usePlanLimits()
  const approachingLimits: Array<{ feature: string; percentage: number }> = []

  // Check events
  if (!isUnlimitedLimit(limits.maxEvents)) {
    const eventsPercentage = (usage.events / limits.maxEvents) * 100
    if (eventsPercentage >= 80) {
      approachingLimits.push({ feature: 'events', percentage: eventsPercentage })
    }
  }

  // Check proposals
  if (!isUnlimitedLimit(limits.maxProposals)) {
    const proposalsPercentage = (usage.proposals / limits.maxProposals) * 100
    if (proposalsPercentage >= 80) {
      approachingLimits.push({ feature: 'proposals', percentage: proposalsPercentage })
    }
  }

  // Check packages
  if (!isUnlimitedLimit(limits.maxPackages)) {
    const packagesPercentage = (usage.packages / limits.maxPackages) * 100
    if (packagesPercentage >= 80) {
      approachingLimits.push({ feature: 'packages', percentage: packagesPercentage })
    }
  }

  return {
    isApproachingAnyLimit: approachingLimits.length > 0,
    approachingLimits,
  }
}
